import { useMemo } from 'react'
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext'
import { useDiagramConnections } from '../../data-binding/hooks/useDiagramConnections'
import { useFolderSourceAuthorities } from '../../data-binding/hooks/useFolderSourceAuthorities'
import { useFolderSourceBindings } from '../../data-binding/hooks/useFolderSourceBindings'
import { resolveReviewedPrimaryConnection } from '../../data-binding/utils/resolveReviewedPrimaryConnection'
import { useDiagramInfo } from '../../../hooks/useDiagramInfo'
import { useFolderData } from '../../../hooks/useFolderData'
import { useUncommittedSummary } from '../../data-binding/hooks/useBoundRecordStore'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'

/**
 * Returns true when the current diagram is in "process repository" mode —
 * i.e. any of the three signals that indicate the user is doing
 * governance / process-intelligence work, not just sketching a flowchart:
 *
 *   1. The folder is explicitly configured for documented/repository work.
 *   2. The folder has a reviewed primary process source connected to the diagram.
 *   3. The folder has source bindings or uncommitted record changes.
 *
 * Mirrors the basic-vs-rich split used by ProcessFolderPanel and the
 * ProcessMappingAssistantPanel so the entire process-domain surface
 * upgrades together when the user crosses the threshold. Casual flowchart
 * users never see governance vocabulary unless they've taken an action
 * that signals repository work.
 *
 * Must be called from inside the diagram document context (i.e. anywhere
 * the editor renders shapes, panels, or property surfaces). The hook
 * trusts the surrounding context and does not catch missing-provider
 * errors — that would mask legitimate setup bugs.
 */
export const useIsProcessRepository = (): boolean => {
  const { diagramId } = useDiagramDocument()
  const {
    isProcessRepositoryEnabled,
    loading: featureFlagsLoading,
  } = useFeatureFlags()
  const showProcessRepository =
    !featureFlagsLoading && isProcessRepositoryEnabled()

  const { data: connectionsData } = useDiagramConnections(
    showProcessRepository ? diagramId : undefined,
  )
  const { data: diagramInfo } = useDiagramInfo(diagramId)
  const folderId = diagramInfo?.folderId ?? undefined
  const authorityFolderId = showProcessRepository ? folderId : undefined
  const { authorities } = useFolderSourceAuthorities(
    authorityFolderId,
    Boolean(authorityFolderId),
  )
  const { bindings } = useFolderSourceBindings(authorityFolderId)
  const primaryConnection = useMemo(() => resolveReviewedPrimaryConnection(
    connectionsData?.connections ?? [],
    authorities,
    bindings,
    'process_inventory',
  ), [authorities, bindings, connectionsData])
  const sourceId = primaryConnection?.dataSourceId
  const { data: folderData } = useFolderData(authorityFolderId)
  const processPurpose = folderData?.folder.metadata?.processPurpose
  const hasRepositoryPurpose =
    typeof processPurpose === 'string' && processPurpose !== 'quick_flowchart'
  const uncommittedSummary = useUncommittedSummary(
    showProcessRepository ? sourceId : undefined,
  )
  const hasRepositorySignals =
    showProcessRepository &&
    (bindings.length > 0 || Boolean(sourceId) || uncommittedSummary.hasChanges)

  return showProcessRepository && (hasRepositoryPurpose || hasRepositorySignals)
}
